/**
 * GitHub OAuth authentication handling
 *
 * The OAuth flow works as follows:
 * 1. User clicks "Connect to GitHub"
 * 2. We redirect to GitHub's OAuth authorize URL
 * 3. User authorizes the app
 * 4. GitHub redirects back with a code
 * 5. We exchange the code for an access token via our serverless function
 * 6. Token is stored locally and used for API calls
 */
const STORAGE_KEY = 'trackmind_github_auth';
const DEFAULT_SCOPE = 'repo';
/**
 * Generates a random state string for OAuth CSRF protection
 */
function generateState() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
/**
 * GitHub OAuth authentication manager
 */
export class GitHubAuth {
    config;
    state;
    stateStorage = new Map();
    constructor(config) {
        this.config = {
            ...config,
            scope: config.scope ?? DEFAULT_SCOPE,
        };
        this.state = this.loadState();
    }
    /**
     * Get current authentication state
     */
    getAuthState() {
        return { ...this.state };
    }
    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        if (!this.state.isAuthenticated || !this.state.token) {
            return false;
        }
        // Check if token has expired
        if (this.state.expiresAt && new Date() > this.state.expiresAt) {
            this.logout();
            return false;
        }
        return true;
    }
    /**
     * Get access token for API calls
     */
    getToken() {
        if (!this.isAuthenticated()) {
            return null;
        }
        return this.state.token;
    }
    /**
     * Start OAuth flow by redirecting to GitHub
     */
    startOAuthFlow() {
        const state = generateState();
        // Store state with timestamp for verification (expires in 10 minutes)
        this.stateStorage.set(state, Date.now());
        // localStorage, not sessionStorage — survives the installed-PWA redirect
        // round-trip (see google/auth.ts for the failure mode)
        localStorage.setItem('oauth_state', state);
        const params = new URLSearchParams({
            client_id: this.config.clientId,
            redirect_uri: this.config.redirectUri,
            scope: this.config.scope,
            state,
        });
        const authUrl = `https://github.com/login/oauth/authorize?${params.toString()}`;
        window.location.href = authUrl;
    }
    /**
     * Handle OAuth callback
     * Call this when the user is redirected back from GitHub
     */
    async handleCallback(code, state) {
        // Verify state to prevent CSRF
        const storedState = localStorage.getItem('oauth_state') ?? sessionStorage.getItem('oauth_state');
        if (!storedState || storedState !== state) {
            throw new Error('Invalid OAuth state - possible CSRF attack');
        }
        localStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_state');
        // Exchange code for token via serverless function
        const tokenResponse = await this.exchangeCodeForToken(code);
        // Calculate expiration if provided
        let expiresAt = null;
        if (tokenResponse.expires_in) {
            expiresAt = new Date(Date.now() + tokenResponse.expires_in * 1000);
        }
        // Fetch user info
        const user = await this.fetchUserInfo(tokenResponse.access_token);
        // Update state
        this.state = {
            isAuthenticated: true,
            token: tokenResponse.access_token,
            user,
            expiresAt,
        };
        this.saveState();
        return true;
    }
    /**
     * Exchange authorization code for access token
     */
    async exchangeCodeForToken(code) {
        const response = await fetch(this.config.tokenExchangeUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code }),
        });
        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Token exchange failed: ${error}`);
        }
        return response.json();
    }
    /**
     * Fetch user info from GitHub API
     */
    async fetchUserInfo(token) {
        const response = await fetch('https://api.github.com/user', {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/vnd.github.v3+json',
            },
        });
        if (!response.ok) {
            throw new Error('Failed to fetch user info');
        }
        const data = await response.json();
        return {
            login: data.login,
            name: data.name,
            avatarUrl: data.avatar_url,
            email: data.email,
        };
    }
    /**
     * Logout and clear stored credentials
     */
    logout() {
        this.state = {
            isAuthenticated: false,
            token: null,
            user: null,
            expiresAt: null,
        };
        this.saveState();
    }
    /**
     * Load authentication state from storage
     */
    loadState() {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) {
                return this.getDefaultState();
            }
            const parsed = JSON.parse(stored);
            return {
                isAuthenticated: parsed.isAuthenticated ?? false,
                token: parsed.token ?? null,
                user: parsed.user ?? null,
                expiresAt: parsed.expiresAt ? new Date(parsed.expiresAt) : null,
            };
        }
        catch {
            return this.getDefaultState();
        }
    }
    /**
     * Save authentication state to storage
     */
    saveState() {
        const toStore = {
            isAuthenticated: this.state.isAuthenticated,
            token: this.state.token,
            user: this.state.user,
            expiresAt: this.state.expiresAt?.toISOString() ?? null,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    }
    /**
     * Get default unauthenticated state
     */
    getDefaultState() {
        return {
            isAuthenticated: false,
            token: null,
            user: null,
            expiresAt: null,
        };
    }
    /**
     * Create a configured GitHubAuth instance from environment
     */
    static createFromEnv() {
        const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID;
        const redirectUri = import.meta.env.VITE_GITHUB_REDIRECT_URI ?? `${window.location.origin}/auth/callback`;
        const tokenExchangeUrl = import.meta.env.VITE_TOKEN_EXCHANGE_URL ?? '/.netlify/functions/oauth';
        if (!clientId) {
            throw new Error('VITE_GITHUB_CLIENT_ID is not configured');
        }
        return new GitHubAuth({
            clientId,
            redirectUri,
            tokenExchangeUrl,
        });
    }
}
/**
 * Parse OAuth callback URL parameters
 */
export function parseCallbackParams(url) {
    const urlObj = new URL(url);
    const code = urlObj.searchParams.get('code');
    const state = urlObj.searchParams.get('state');
    if (!code || !state) {
        return null;
    }
    // This parser accepts any code+state pair, so it would happily claim a
    // calendar-connect redirect and then fail its state check — burning the
    // authorization code in the process. Calendar states are prefixed; leave
    // them for the calendar handler.
    if (state.startsWith('cal.')) {
        return null;
    }
    return { code, state };
}
/**
 * Check if current URL is an OAuth callback
 */
export function isOAuthCallback(url) {
    return parseCallbackParams(url) !== null;
}
//# sourceMappingURL=auth.js.map